import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Device as TwilioDevice } from '@twilio/voice-sdk';
import { Button, Card, Select } from '../ui/Kit';
import { api, supabase } from '../../lib/api';
import Spinner from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import DncConfirmModal from '../ui/DncConfirmModal';
import RoomTypeSelector from './RoomTypeSelector';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiPhoneOff, FiX } = FiIcons;

const DEFAULT_DIALER = 'twilio';

/* -----------------------------------------------------------------------
 * Telnyx adapter
 * ---------------------------------------------------------------------*/

/** @telnyx/webrtc call state enum (numeric) — not re-exported from the browser bundle. */
const TelnyxCallState = {
  New: 0,
  Requesting: 1,
  Trying: 2,
  Recovering: 3,
  Ringing: 4,
  Answering: 5,
  Early: 6,
  Active: 7,
  Held: 8,
  Hangup: 9,
  Destroy: 10,
  Purge: 11,
};

/** Reverse lookup so console logs print "Active" instead of `7`. */
const TELNYX_CALL_STATE_NAME = Object.fromEntries(
  Object.entries(TelnyxCallState).map(([k, v]) => [v, k]),
);

function describeTelnyxCallState(state) {
  if (typeof state === 'number') return `${TELNYX_CALL_STATE_NAME[state] ?? 'Unknown'}(${state})`;
  return String(state);
}

/** Plain-object snapshot of the Telnyx SDK Call so we can dump the relevant diagnostic fields. */
function summarizeTelnyxCall(call) {
  if (!call) return null;
  return {
    id: call.id,
    direction: call.direction,
    state: describeTelnyxCallState(call.state),
    prevState: describeTelnyxCallState(call.prevState),
    cause: call.cause,
    causeCode: call.causeCode,
    sipCode: call.sipCode,
    sipReason: call.sipReason,
    byeCause: call.byeCause,
    byeCauseCode: call.byeCauseCode,
    destinationNumber: call.options?.destinationNumber,
    callerNumber: call.options?.callerNumber,
    telnyxIDs: call.telnyxIDs,
  };
}

function mapTelnyxStateToUi(call) {
  const s = call?.state;
  if (
    s === TelnyxCallState.New ||
    s === TelnyxCallState.Requesting ||
    s === TelnyxCallState.Trying ||
    s === TelnyxCallState.Recovering
  ) {
    return 'connecting';
  }
  if (
    s === TelnyxCallState.Ringing ||
    s === TelnyxCallState.Answering ||
    s === TelnyxCallState.Early
  ) {
    return 'ringing';
  }
  if (s === TelnyxCallState.Active || s === TelnyxCallState.Held) return 'connected';
  if (
    s === TelnyxCallState.Hangup ||
    s === TelnyxCallState.Destroy ||
    s === TelnyxCallState.Purge ||
    s === 'hangup' ||
    s === 'destroy'
  ) {
    return 'ended';
  }
  if (typeof s === 'string') {
    const lower = s.toLowerCase();
    if (lower.includes('ring')) return 'ringing';
    if (lower === 'active' || lower === 'held') return 'connected';
    if (lower.includes('hang') || lower.includes('destroy')) return 'ended';
    if (lower.includes('try') || lower.includes('request')) return 'connecting';
  }
  return 'connecting';
}

function deriveTelnyxOutcome(wasConnected, call) {
  if (wasConnected) return 'answered';
  const code = call?.sipCode;
  if (code === 486) return 'busy';
  if (code === 603 || code === 487) return 'rejected';
  const cause = call?.cause?.toLowerCase?.() ?? '';
  if (cause.includes('busy')) return 'busy';
  return 'no_answer';
}

/** Normalize dial strings for Telnyx (digits, optional leading country code). */
function dialableTelnyxDestination(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `1${digits}`;
  return digits;
}

/* -----------------------------------------------------------------------
 * Twilio adapter
 * ---------------------------------------------------------------------*/

/**
 * Diagnostic-only JWT decoder. Twilio rejects access tokens with opaque `AccessToken` errors
 * (20101–20156) on the wire, so logging the parsed header + payload locally lets us see the
 * `iss`, `sub`, `grants`, and `exp` claims that Twilio actually saw without copy-pasting the
 * token into jwt.io. Never trust these claims for any code path — Twilio is the verifier.
 */
function decodeTwilioTokenForLogging(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return { error: `unexpected JWT segment count: ${parts.length}` };
  const decodeSegment = (seg) => {
    try {
      const padded = seg.replace(/-/g, '+').replace(/_/g, '/');
      const padLen = (4 - (padded.length % 4)) % 4;
      const decoded = atob(padded + '='.repeat(padLen));
      return JSON.parse(decoded);
    } catch (e) {
      return { decode_error: e?.message || String(e) };
    }
  };
  const header = decodeSegment(parts[0]);
  const payload = decodeSegment(parts[1]);
  const grants = payload?.grants ?? null;
  return {
    header,
    payload: {
      iss: payload?.iss,
      sub: payload?.sub,
      jti: payload?.jti,
      iat: payload?.iat,
      nbf: payload?.nbf,
      exp: payload?.exp,
      identity: grants?.identity,
      voice_grant: grants?.voice ?? null,
    },
    iss_looks_like_api_key: typeof payload?.iss === 'string' && payload.iss.startsWith('SK'),
    iss_looks_like_account_sid: typeof payload?.iss === 'string' && payload.iss.startsWith('AC'),
  };
}

/**
 * @twilio/voice-sdk Call.status() returns one of: 'pending', 'connecting', 'ringing',
 * 'open', 'closed'. Map those onto the same UI vocabulary the Telnyx adapter uses so the
 * Softphone JSX is provider-neutral.
 */
function mapTwilioStatusToUi(status) {
  switch (status) {
    case 'pending':
    case 'connecting':
      return 'connecting';
    case 'ringing':
      return 'ringing';
    case 'open':
      return 'connected';
    case 'closed':
      return 'ended';
    default:
      return 'connecting';
  }
}

/**
 * Twilio Voice SDK errors are instances of `TwilioError` with a stable `code` and a
 * lookup-able description. The most common registration failures are:
 *   20101  Invalid Access Token — malformed JWT (bad header, missing claims).
 *   20102  Invalid Access Token header.
 *   20103  Invalid Access Token issuer/subject — token signed against the wrong SID.
 *   20104  Access Token expired or expiration date invalid.
 *   20107  Invalid Access Token signature — usually means signed with the wrong secret.
 *   20151  AccessToken authentication failed — signing with TWILIO_AUTH_TOKEN instead of
 *          a real Twilio API Key (SK… SID + secret) is the canonical cause; Twilio's Voice
 *          service requires API Keys, not the account auth token, even though twilio-node
 *          accepts either when minting.
 *   20156  AccessToken grants invalid — voice grant missing or malformed.
 *   31204  Invalid `application_sid` — TWILIO_TWIML_APP_SID is wrong or unowned by this account.
 */
function summarizeTwilioError(err) {
  if (!err) return null;
  return {
    name: err.name,
    code: err.code ?? null,
    message: err.message,
    description: err.description,
    explanation: err.explanation,
    causes: err.causes,
    solutions: err.solutions,
    twilioError: err.twilioError
      ? { code: err.twilioError.code, message: err.twilioError.message }
      : undefined,
    originalError: err.originalError
      ? { name: err.originalError.name, message: err.originalError.message }
      : undefined,
  };
}

function summarizeTwilioCall(call) {
  if (!call) return null;
  let status = 'unknown';
  try {
    status = typeof call.status === 'function' ? call.status() : call.status;
  } catch {
    /* keep status */
  }
  let parameters = null;
  try {
    parameters = typeof call.parameters === 'function' ? call.parameters() : call.parameters;
  } catch {
    /* keep parameters */
  }
  return {
    direction: call.direction,
    status,
    sid: parameters?.CallSid ?? call.callSid ?? null,
    from: parameters?.From ?? null,
    to: parameters?.To ?? null,
  };
}

function deriveTwilioOutcome(wasConnected, terminationReason) {
  if (wasConnected) return 'answered';
  const reason = String(terminationReason ?? '').toLowerCase();
  if (reason.includes('busy')) return 'busy';
  if (reason.includes('reject') || reason.includes('declin')) return 'rejected';
  if (reason.includes('cancel')) return 'rejected';
  return 'no_answer';
}

/**
 * Twilio's TwiML `<Dial><Number>` accepts E.164 numbers; the `+` prefix is required for
 * non-North-American destinations and is harmless for NANP. Mirror the Telnyx adapter's
 * "10 digits → assume US" affordance so the UI's input box behaves the same regardless
 * of provider.
 */
function dialableTwilioDestination(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

/* -----------------------------------------------------------------------
 * Shared helpers
 * ---------------------------------------------------------------------*/

function formatMmSs(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

const STATUS_LABEL = {
  idle: 'Idle',
  fetching_token: 'Initializing…',
  registering: 'Connecting…',
  ready: 'Ready',
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  connected: 'Connected',
  ended: 'Ended',
  error: 'Error',
};

const POST_CALL_OUTCOME_OPTIONS = [
  { value: 'answered', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'callback_requested', label: 'Callback Requested' },
  { value: 'booked_meeting', label: 'Booked Meeting' },
  { value: 'closed_business', label: 'Closed Business' },
];

const POST_CALL_LEAD_STATUS_OPTIONS = [
  { value: 'no_change', label: 'No Change' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'dnc', label: 'DNC' },
];

function mapDerivedOutcomeToMenu(derived) {
  if (derived === 'answered') return 'answered';
  if (derived === 'voicemail' || derived === 'machine') return 'voicemail';
  if (derived === 'busy') return 'busy';
  if (derived === 'callback_requested') return 'callback_requested';
  return 'no_answer';
}

/**
 * Browser softphone. Reads the agent's `preferred_dialer` (telnyx | twilio) and initializes
 * the matching SDK on mount. The UI (status text, dial input, call/hangup buttons, timer)
 * is identical regardless of provider — switching dialers is a backend concern.
 */
export default function Softphone({
  open,
  onClose,
  leadId,
  leadPhone,
  leadName,
  leadStatus,
  onAfterPostCallSave,
}) {
  const { addToast } = useToast();
  const [phase, setPhase] = useState('idle');
  const [uiStatus, setUiStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [callerId, setCallerId] = useState('');
  const [destination, setDestination] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [dialer, setDialer] = useState(DEFAULT_DIALER);

  const [postCallCtx, setPostCallCtx] = useState(null);
  const [postCallOutcome, setPostCallOutcome] = useState('no_answer');
  const [postCallLeadStatus, setPostCallLeadStatus] = useState('no_change');
  const [postCallNotes, setPostCallNotes] = useState('');
  const [postCallRoomType, setPostCallRoomType] = useState(null);
  const [postCallSaving, setPostCallSaving] = useState(false);
  const [postCallDncOpen, setPostCallDncOpen] = useState(false);

  const clientRef = useRef(null);
  const activeCallRef = useRef(null);
  const wasConnectedRef = useRef(false);
  const timerRef = useRef(null);
  const finalizedRef = useRef(false);
  const profileRef = useRef(null);
  const durationStartRef = useRef(null);
  const dialerRef = useRef(DEFAULT_DIALER);
  const twilioTerminationReasonRef = useRef(null);
  const leadIdRef = useRef(leadId);
  leadIdRef.current = leadId;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Both SDKs need different teardown calls; doing the wrong one silently leaks the
   * underlying WebSocket and microphone. Branch on the dialer the client was created with
   * (not the current state — the user could have toggled dialers since).
   */
  const teardownClient = useCallback(() => {
    stopTimer();
    const activeDialer = dialerRef.current;
    const call = activeCallRef.current;
    if (call) {
      try {
        if (activeDialer === 'twilio') {
          call.disconnect?.();
        } else {
          call.hangup?.();
        }
      } catch {
        /* ignore */
      }
    }
    activeCallRef.current = null;

    const client = clientRef.current;
    if (client) {
      try {
        if (activeDialer === 'twilio') {
          client.destroy?.();
        } else {
          client.disconnect?.();
        }
      } catch {
        /* ignore */
      }
    }
    clientRef.current = null;
  }, [stopTimer]);

  const finalizeCall = useCallback(
    async (call) => {
      const lid = leadIdRef.current;
      if (finalizedRef.current || !lid) return;
      finalizedRef.current = true;
      stopTimer();

      const activeDialer = dialerRef.current;
      const direction = call?.direction === 'inbound' ? 'inbound' : 'outbound';
      const durationSeconds = durationStartRef.current
        ? Math.max(0, Math.floor((Date.now() - durationStartRef.current) / 1000))
        : 0;
      durationStartRef.current = null;

      let insertedRow = null;
      let suggestedOutcome = 'no_answer';
      try {
        if (activeDialer === 'twilio') {
          const outcome = deriveTwilioOutcome(
            wasConnectedRef.current,
            twilioTerminationReasonRef.current,
          );
          suggestedOutcome = mapDerivedOutcomeToMenu(outcome);
          let parameters = null;
          try {
            parameters =
              typeof call?.parameters === 'function' ? call.parameters() : call?.parameters;
          } catch {
            /* keep parameters */
          }
          insertedRow = await api.calls.log(lid, {
            direction,
            outcome,
            duration_seconds: durationSeconds,
            notes: parameters?.CallSid ? `Twilio CallSid: ${parameters.CallSid}` : null,
          });
        } else {
          const ids = call?.telnyxIDs ?? {};
          const controlId = ids.telnyxCallControlId ?? null;
          const sessionId = ids.telnyxSessionId ?? null;
          const outcome = deriveTelnyxOutcome(wasConnectedRef.current, call);
          suggestedOutcome = mapDerivedOutcomeToMenu(outcome);
          insertedRow = await api.calls.logTelnyxCallEnd(lid, {
            direction,
            outcome,
            duration_seconds: durationSeconds,
            telnyx_call_control_id: controlId,
            telnyx_call_session_id: sessionId,
          });
        }
      } catch (e) {
        console.error('[softphone] failed to log call', e);
      }

      if (insertedRow?.id) {
        setPostCallCtx({
          logId: insertedRow.id,
          baseNotes: insertedRow.notes || '',
          suggestedOutcome,
        });
        setPostCallOutcome(suggestedOutcome);
        setPostCallLeadStatus('no_change');
        setPostCallNotes('');
        setPostCallRoomType(null);
      }

      setUiStatus('ended');
      setPhase('ready');
    },
    [stopTimer],
  );

  const dismissPostCallPanel = useCallback(() => {
    setPostCallCtx(null);
    setPostCallDncOpen(false);
    setPostCallSaving(false);
    setUiStatus('ready');
  }, []);

  const commitPostCallSave = useCallback(async () => {
    if (!postCallCtx?.logId) return;
    setPostCallSaving(true);
    try {
      const mergedNotes =
        [postCallCtx.baseNotes?.trim(), postCallNotes.trim()].filter(Boolean).join('\n\n') || null;
      const patch = {
        outcome: postCallOutcome,
        notes: mergedNotes,
      };
      if (postCallRoomType) patch.room_type = postCallRoomType;
      await api.calls.updateLog(postCallCtx.logId, patch);

      const lid = leadIdRef.current;
      if (postCallLeadStatus !== 'no_change' && lid) {
        await api.leads.update(lid, { status: postCallLeadStatus });
      }

      addToast({ type: 'success', title: 'Call log saved' });
      dismissPostCallPanel();
      onAfterPostCallSave?.();
    } catch (e) {
      console.error('[softphone] post-call save failed', e);
      addToast({
        type: 'error',
        title: 'Could not save',
        message: e?.message || 'Unknown error',
      });
    } finally {
      setPostCallSaving(false);
    }
  }, [
    postCallCtx,
    postCallNotes,
    postCallOutcome,
    postCallLeadStatus,
    postCallRoomType,
    addToast,
    dismissPostCallPanel,
    onAfterPostCallSave,
  ]);

  /** Re-used by both adapters once they detect "Active"; stamping the start time twice
   * would over-count the duration so guard with `durationStartRef`. */
  const beginConnectedTicker = useCallback(() => {
    wasConnectedRef.current = true;
    setUiStatus('connected');
    if (!durationStartRef.current) {
      durationStartRef.current = Date.now();
    }
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        if (durationStartRef.current) {
          setElapsedSec(Math.floor((Date.now() - durationStartRef.current) / 1000));
        }
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      teardownClient();
      setPhase('idle');
      setUiStatus('idle');
      setErrorMsg('');
      setElapsedSec(0);
      finalizedRef.current = false;
      wasConnectedRef.current = false;
      twilioTerminationReasonRef.current = null;
      durationStartRef.current = null;
      setPostCallCtx(null);
      setPostCallDncOpen(false);
      setPostCallSaving(false);
      setPostCallNotes('');
      return;
    }

    finalizedRef.current = false;
    wasConnectedRef.current = false;
    twilioTerminationReasonRef.current = null;
    durationStartRef.current = null;

    if (!supabase) {
      setPhase('error');
      setUiStatus('error');
      setErrorMsg('Supabase is not configured; calls are unavailable in this environment.');
      return;
    }

    let cancelled = false;

    (async () => {
      setPhase('fetching_token');
      setUiStatus('fetching_token');
      setErrorMsg('');

      try {
        const profile = await api.auth.getCurrentUser();
        if (cancelled) return;
        profileRef.current = profile;

        const chosen =
          profile?.preferred_dialer === 'telnyx' || profile?.preferred_dialer === 'twilio'
            ? profile.preferred_dialer
            : DEFAULT_DIALER;
        setDialer(chosen);
        dialerRef.current = chosen;

        // Pre-fill destination using the provider's dial-string conventions so the user
        // sees a sensible number on first paint.
        setDestination(
          chosen === 'twilio'
            ? dialableTwilioDestination(leadPhone)
            : dialableTelnyxDestination(leadPhone),
        );

        if (chosen === 'twilio') {
          await initializeTwilio({ cancelled, profile });
        } else {
          await initializeTelnyx({ cancelled, profile });
        }
      } catch (e) {
        if (!cancelled) {
          setPhase('error');
          setUiStatus('error');
          setErrorMsg(e?.message || 'Could not start softphone');
        }
      }
    })();

    /* eslint-disable no-inner-declarations */
    async function initializeTelnyx({ cancelled, profile }) {
      const { token, caller_id_number } = await api.telnyx.getWebRtcToken();
      if (cancelled) return;
      console.log(
        '[softphone:telnyx] received WebRTC login_token (length=%d) caller_id=%s',
        token?.length ?? 0,
        caller_id_number,
      );
      setCallerId(caller_id_number || '');

      const client = new TelnyxRTC({
        login_token: token,
        ringtoneFile: undefined,
        ringbackFile: undefined,
      });
      client.remoteElement = 'softphone-remote-audio';
      clientRef.current = client;

      client.on('telnyx.error', (err) => {
        console.error('[softphone:telnyx] error', {
          message: err?.error?.message || err?.message,
          code: err?.error?.code || err?.code,
          cause: err?.cause,
          raw: err,
        });
        setErrorMsg(err?.error?.message || err?.message || 'Telnyx error');
        setUiStatus('error');
      });

      client.on('telnyx.ready', () => {
        console.log('[softphone:telnyx] ready — registered with SIP gateway');
        if (!cancelled) {
          setPhase('ready');
          setUiStatus('ready');
        }
      });

      client.on('telnyx.socket.open', () => console.log('[softphone:telnyx] socket.open'));
      client.on('telnyx.socket.close', (evt) =>
        console.warn('[softphone:telnyx] socket.close', {
          code: evt?.code,
          reason: evt?.reason,
          wasClean: evt?.wasClean,
        }),
      );
      client.on('telnyx.socket.error', (evt) =>
        console.error('[softphone:telnyx] socket.error', evt),
      );

      client.on('telnyx.notification', (notification) => {
        const call = notification.call;
        console.log('[softphone:telnyx] notification', {
          type: notification.type,
          call: summarizeTelnyxCall(call),
        });

        if (notification.type === 'callUpdate' && call) {
          activeCallRef.current = call;
          const mapped = mapTelnyxStateToUi(call);
          console.log(
            '[softphone:telnyx] callUpdate %s -> %s (ui=%s) cause=%s sip=%s/%s',
            describeTelnyxCallState(call.prevState),
            describeTelnyxCallState(call.state),
            mapped,
            call.cause ?? '-',
            call.sipCode ?? '-',
            call.sipReason ?? '-',
          );

          if (mapped === 'connected') {
            beginConnectedTicker();
          } else if (mapped === 'ringing') {
            setUiStatus('ringing');
          } else if (mapped === 'connecting') {
            setUiStatus('connecting');
          } else if (mapped === 'ended') {
            const elapsedMs = durationStartRef.current ? Date.now() - durationStartRef.current : 0;
            const endedBeforeAnswer = !wasConnectedRef.current;
            console.warn('[softphone:telnyx] call ended', {
              wasConnected: wasConnectedRef.current,
              elapsedMs,
              cause: call.cause,
              causeCode: call.causeCode,
              sipCode: call.sipCode,
              sipReason: call.sipReason,
              byeCause: call.byeCause,
              byeCauseCode: call.byeCauseCode,
            });
            // Early teardown (Destroy/Hangup before Active) is the exact symptom of a JWT
            // minted against the wrong connection type or a credential_connection missing
            // an outbound_voice_profile_id. Surface it loudly so the failure is obvious in
            // the browser console even when the SDK silently falls through callUpdate.
            if (endedBeforeAnswer) {
              console.error(
                '[softphone:telnyx] call torn down before Active — likely SIP routing/auth issue',
                {
                  state: describeTelnyxCallState(call.state),
                  prevState: describeTelnyxCallState(call.prevState),
                  cause: call.cause,
                  causeCode: call.causeCode,
                  sipCode: call.sipCode,
                  sipReason: call.sipReason,
                  byeCause: call.byeCause,
                  byeCauseCode: call.byeCauseCode,
                  telnyxIDs: call.telnyxIDs,
                },
              );
            }
            finalizeCall(call);
          }

          if (
            call?.direction === 'inbound' &&
            (call.state === TelnyxCallState.Ringing || call.state === 'ringing')
          ) {
            call.answer?.().catch((err) =>
              console.error('[softphone:telnyx] inbound auto-answer failed', err),
            );
          }
        } else if (notification.type === 'userMediaError') {
          console.error('[softphone:telnyx] userMediaError', notification);
          setErrorMsg(notification.error?.message || 'Microphone access error');
          setUiStatus('error');
        }
      });

      setPhase('registering');
      setUiStatus('registering');
      console.log('[softphone:telnyx] connecting TelnyxRTC client…');
      client.connect();
      // profile arg is unused; kept for signature parity with initializeTwilio.
      void profile;
    }

    async function initializeTwilio({ cancelled, profile }) {
      const tokenResponse = await api.telnyx.getTwilioToken();
      if (cancelled) return;

      const {
        token,
        caller_id_number,
        twiml_app_configured,
        signing_mode,
        identity,
        ttl,
      } = tokenResponse ?? {};

      // Decode-only diagnostics. If `iss` does not start with `SK` (a real Twilio API Key),
      // Twilio's Voice service will reject device.register() with code 20151 even though the
      // JWT itself is well-formed. Log this loudly so the operator can swap to API Keys.
      const decoded = decodeTwilioTokenForLogging(token);
      console.log('[softphone:twilio] token response from twilio-token Edge Function', {
        signing_mode,
        twiml_app_configured,
        identity,
        caller_id_number,
        ttl,
        token_length: typeof token === 'string' ? token.length : 0,
        token_prefix: typeof token === 'string' ? `${token.slice(0, 12)}…` : null,
        decoded,
      });

      if (signing_mode === 'auth_token') {
        console.warn(
          '[softphone:twilio] twilio-token Edge Function signed the JWT with TWILIO_AUTH_TOKEN. ' +
          'Twilio Voice typically requires a real API Key (SK…) — registration may fail with ' +
          'TwilioError 20151 (AccessToken authentication failed). Create an API Key in the Twilio ' +
          'console and set TWILIO_API_KEY_SID + TWILIO_API_KEY_SECRET in Supabase Edge secrets.',
        );
      }
      if (!twiml_app_configured) {
        console.warn(
          '[softphone:twilio] TWILIO_TWIML_APP_SID is not set in Edge secrets. The device may ' +
          'register, but outbound calls will fail with TwilioError 31204 (invalid app_sid). ' +
          'Create a TwiML Application in the Twilio console pointing at the twilio-voice ' +
          'Edge Function and set its SID as TWILIO_TWIML_APP_SID.',
        );
      }
      if (decoded?.iss_looks_like_account_sid && !decoded?.iss_looks_like_api_key) {
        console.warn(
          '[softphone:twilio] JWT iss starts with "AC" (Account SID), not "SK" (API Key). ' +
          'Twilio Voice rejects this with 20151. Mint the token with an API Key.',
        );
      }

      if (!token) {
        setPhase('error');
        setUiStatus('error');
        setErrorMsg('twilio-token returned no token');
        return;
      }

      setCallerId(caller_id_number || '');

      // Bump logLevel to 'debug' so the SDK emits its own WebSocket / signaling diagnostics
      // alongside our wrappers. Production can knock this back to 'warn'.
      const device = new TwilioDevice(token, {
        edge: 'ashburn',
        logLevel: 'debug',
        allowIncomingWhileBusy: false,
      });
      clientRef.current = device;

      device.on('error', (err, call) => {
        const summary = summarizeTwilioError(err);
        console.error('[softphone:twilio] device error event', {
          ...summary,
          call: summarizeTwilioCall(call),
        });
        // Surface the numeric code in the user-facing error so the operator can look it up
        // (e.g. "Twilio error 20151: AccessToken authentication failed").
        const codePrefix = summary?.code ? `Twilio error ${summary.code}: ` : 'Twilio error: ';
        setErrorMsg(`${codePrefix}${summary?.message || 'unknown error'}`);
        setUiStatus('error');
      });

      device.on('registered', () => {
        console.log('[softphone:twilio] device registered with Twilio');
        if (!cancelled) {
          setPhase('ready');
          setUiStatus('ready');
        }
      });

      device.on('unregistered', () => {
        console.warn('[softphone:twilio] device unregistered');
      });

      device.on('registering', () => {
        console.log('[softphone:twilio] device registering…');
      });

      device.on('tokenWillExpire', () => {
        console.warn(
          '[softphone:twilio] access token will expire soon; not refreshing automatically. Close and reopen the dialer to mint a fresh token.',
        );
      });

      device.on('incoming', (call) => {
        console.log('[softphone:twilio] incoming call', summarizeTwilioCall(call));
        wireTwilioCallEvents(call);
        activeCallRef.current = call;
        // Mirror the Telnyx adapter: auto-answer inbound calls so the agent doesn't have
        // to click an extra Accept button.
        try {
          call.accept();
        } catch (err) {
          console.error('[softphone:twilio] inbound accept failed', err);
        }
      });

      setPhase('registering');
      setUiStatus('registering');
      console.log('[softphone:twilio] calling device.register()…');
      try {
        await device.register();
        console.log('[softphone:twilio] device.register() resolved');
      } catch (err) {
        const summary = summarizeTwilioError(err);
        console.error('[softphone:twilio] device.register() threw', {
          ...summary,
          // Re-log the decoded JWT here so the failure and the token claims appear together
          // in the console output, with no need to scroll up to correlate them.
          token_decoded: decoded,
          signing_mode,
        });
        const codePrefix = summary?.code ? `Twilio error ${summary.code}: ` : '';
        let hint = '';
        if (summary?.code === 20151 || summary?.code === 20107 || summary?.code === 20103) {
          hint =
            ' (likely the JWT is signed with TWILIO_AUTH_TOKEN; create a Twilio API Key and ' +
            'set TWILIO_API_KEY_SID + TWILIO_API_KEY_SECRET in Edge secrets)';
        } else if (summary?.code === 20156) {
          hint = ' (voice grant missing/invalid — check the twilio-token Edge Function)';
        } else if (summary?.code === 20104 || summary?.code === 20105) {
          hint = ' (token expired or not yet valid — check Edge Function clock and TTL)';
        }
        setErrorMsg(
          `${codePrefix}${summary?.message || err?.message || 'Could not register Twilio device'}${hint}`,
        );
        setUiStatus('error');
        setPhase('error');
      }
      void profile;
    }

    function wireTwilioCallEvents(call) {
      call.on('ringing', (hasEarlyMedia) => {
        console.log('[softphone:twilio] ringing', { hasEarlyMedia });
        setUiStatus('ringing');
      });
      call.on('accept', () => {
        console.log('[softphone:twilio] accept (call open)');
        beginConnectedTicker();
      });
      call.on('reconnecting', (err) => {
        console.warn('[softphone:twilio] reconnecting', err);
      });
      call.on('reconnected', () => {
        console.log('[softphone:twilio] reconnected');
      });
      call.on('warning', (name, data) => {
        console.warn('[softphone:twilio] warning', name, data);
      });
      call.on('disconnect', () => {
        console.log('[softphone:twilio] disconnect', summarizeTwilioCall(call));
        if (!twilioTerminationReasonRef.current) {
          twilioTerminationReasonRef.current = 'disconnect';
        }
        finalizeCall(call);
      });
      call.on('cancel', () => {
        console.warn('[softphone:twilio] cancel');
        twilioTerminationReasonRef.current = 'cancel';
        finalizeCall(call);
      });
      call.on('reject', () => {
        console.warn('[softphone:twilio] reject');
        twilioTerminationReasonRef.current = 'reject';
        finalizeCall(call);
      });
      call.on('error', (err) => {
        const summary = summarizeTwilioError(err);
        console.error('[softphone:twilio] call error event', summary);
        twilioTerminationReasonRef.current = summary?.message || 'error';
        const status = mapTwilioStatusToUi(
          typeof call.status === 'function' ? call.status() : call.status,
        );
        if (status !== 'connected' && status !== 'ringing') {
          const prefix = summary?.code ? `Twilio error ${summary.code}: ` : '';
          setErrorMsg(`${prefix}${summary?.message || 'Call failed'}`);
          setUiStatus('error');
        }
      });
    }
    /* eslint-enable no-inner-declarations */

    return () => {
      cancelled = true;
      teardownClient();
    };
  }, [open, leadPhone, teardownClient, finalizeCall, beginConnectedTicker]);

  const handleCall = async () => {
    const profile = profileRef.current;
    const client = clientRef.current;
    const activeDialer = dialerRef.current;
    if (!client || !profile?.agency_id) return;

    const dest =
      activeDialer === 'twilio'
        ? dialableTwilioDestination(destination)
        : dialableTelnyxDestination(destination);
    if (!dest) return;

    setElapsedSec(0);
    durationStartRef.current = null;
    wasConnectedRef.current = false;
    finalizedRef.current = false;
    twilioTerminationReasonRef.current = null;

    setUiStatus('connecting');

    if (activeDialer === 'twilio') {
      try {
        console.log('[softphone:twilio] placing outbound call', {
          To: dest,
          callerNumber: callerId || undefined,
          leadId,
        });
        // The TwiML application backing this token is responsible for reading
        // these custom params and emitting <Dial><Number callerId={From}>{To}</Number></Dial>.
        // If TWILIO_TWIML_APP_SID is not configured server-side, this connect call will
        // succeed locally but Twilio will reject the leg.
        const params = {
          To: dest,
          From: callerId || '',
          LeadId: leadId ?? '',
          AgentId: profile.id ?? '',
          AgencyId: profile.agency_id ?? '',
        };
        const call = await client.connect({ params });
        activeCallRef.current = call;
        // The "incoming" listener wires events on inbound; outbound calls have to be
        // wired manually on the returned Call object.
        wireTwilioOutboundCallEvents(call);
        console.log('[softphone:twilio] connect returned', summarizeTwilioCall(call));
      } catch (e) {
        console.error('[softphone:twilio] connect threw', e);
        setErrorMsg(e?.message || 'Failed to place call');
        setUiStatus('error');
      }
      return;
    }

    const clientStatePayload = {
      lead_id: leadId,
      agent_id: profile.id,
      agency_id: profile.agency_id,
    };
    const clientState = btoa(JSON.stringify(clientStatePayload));

    try {
      console.log('[softphone:telnyx] placing outbound call', {
        destinationNumber: dest,
        callerNumber: callerId || undefined,
        leadId,
      });
      const call = client.newCall({
        destinationNumber: dest,
        callerNumber: callerId || undefined,
        clientState,
        remoteElement: 'softphone-remote-audio',
        audio: true,
        video: false,
      });
      console.log('[softphone:telnyx] newCall returned', summarizeTelnyxCall(call));
    } catch (e) {
      console.error('[softphone:telnyx] newCall threw', e);
      setErrorMsg(e?.message || 'Failed to place call');
      setUiStatus('error');
    }
  };

  /**
   * Wire Twilio Call events for an outbound call. Same listeners as `wireTwilioCallEvents`
   * inside the init effect, but we cannot reach that helper from `handleCall` because it
   * is closed over by the effect. Duplicating ~30 lines is cheaper than promoting all
   * setters/refs into module scope.
   */
  function wireTwilioOutboundCallEvents(call) {
    call.on('ringing', () => setUiStatus('ringing'));
    call.on('accept', () => beginConnectedTicker());
    call.on('disconnect', () => {
      if (!twilioTerminationReasonRef.current) {
        twilioTerminationReasonRef.current = 'disconnect';
      }
      finalizeCall(call);
    });
    call.on('cancel', () => {
      twilioTerminationReasonRef.current = 'cancel';
      finalizeCall(call);
    });
    call.on('reject', () => {
      twilioTerminationReasonRef.current = 'reject';
      finalizeCall(call);
    });
    call.on('error', (err) => {
      const summary = summarizeTwilioError(err);
      console.error('[softphone:twilio] outbound call error event', summary);
      twilioTerminationReasonRef.current = summary?.message || 'error';
      const prefix = summary?.code ? `Twilio error ${summary.code}: ` : '';
      setErrorMsg(`${prefix}${summary?.message || 'Call failed'}`);
      setUiStatus('error');
    });
  }

  const handleHangup = async () => {
    const call = activeCallRef.current;
    const activeDialer = dialerRef.current;
    console.log(
      '[softphone:%s] user requested hangup',
      activeDialer,
      activeDialer === 'twilio' ? summarizeTwilioCall(call) : summarizeTelnyxCall(call),
    );
    try {
      if (activeDialer === 'twilio') {
        call?.disconnect?.();
      } else {
        await call?.hangup?.();
      }
    } catch (e) {
      console.error('[softphone] hangup threw', e);
    }
    if (call) await finalizeCall(call);
    else {
      finalizedRef.current = false;
      stopTimer();
      durationStartRef.current = null;
      setElapsedSec(0);
      setUiStatus('ready');
      setPhase('ready');
    }
  };

  const handlePostCallSaveClick = () => {
    if (postCallLeadStatus === 'dnc') {
      setPostCallDncOpen(true);
      return;
    }
    void commitPostCallSave();
  };

  if (!open) return null;

  const busyCalling =
    uiStatus === 'connecting' || uiStatus === 'ringing' || uiStatus === 'connected';
  const postCallBlocking = Boolean(postCallCtx);

  const dialerLabel = dialer === 'twilio' ? 'Twilio Voice' : 'Telnyx WebRTC';

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-4 bg-black/40 backdrop-blur-[2px]">
      <audio id="softphone-remote-audio" autoPlay playsInline className="hidden" />

      <Card className="w-full max-w-md shadow-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Softphone · {dialerLabel}
            </p>
            <p className="text-sm font-semibold text-gray-900 truncate max-w-[240px]">
              {leadName || 'Lead'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              teardownClient();
              onClose?.();
            }}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {errorMsg && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
              {errorMsg}
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Destination</label>
            <input
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="+1…"
              disabled={busyCalling || postCallBlocking || phase === 'fetching_token' || phase === 'registering'}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className="text-sm font-semibold text-gray-900">
                {STATUS_LABEL[uiStatus] ?? uiStatus}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-lg font-mono font-semibold text-gray-900 tabular-nums">
                {busyCalling || uiStatus === 'ended' ? formatMmSs(elapsedSec) : '00:00'}
              </p>
            </div>
          </div>

          {(phase === 'fetching_token' || phase === 'registering') && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Spinner className="text-primary" />
              Connecting…
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              className="flex-1"
              onClick={handleCall}
              disabled={
                !destination.trim() ||
                phase !== 'ready' ||
                postCallBlocking ||
                uiStatus === 'connecting' ||
                uiStatus === 'ringing' ||
                uiStatus === 'connected'
              }
            >
              <SafeIcon icon={FiPhone} className="mr-2" />
              Call
            </Button>
            <Button
              type="button"
              variant="danger"
              className="flex-1"
              onClick={handleHangup}
              disabled={!busyCalling}
            >
              <SafeIcon icon={FiPhoneOff} className="mr-2" />
              Hang up
            </Button>
          </div>

          {postCallCtx && (
            <div className="rounded-lg border border-primary/30 bg-sky-50/80 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-900">Log this call</p>
              <Select
                label="Call outcome"
                options={POST_CALL_OUTCOME_OPTIONS}
                value={postCallOutcome}
                onChange={(e) => setPostCallOutcome(e.target.value)}
                disabled={postCallSaving}
              />
              <RoomTypeSelector
                value={postCallRoomType}
                onChange={setPostCallRoomType}
                disabled={postCallSaving}
              />
              <div>
                <Select
                  label="Lead status"
                  options={POST_CALL_LEAD_STATUS_OPTIONS}
                  value={postCallLeadStatus}
                  onChange={(e) => setPostCallLeadStatus(e.target.value)}
                  disabled={postCallSaving}
                />
                {leadStatus ? (
                  <p className="text-[11px] text-gray-500 mt-1">
                    On record: {String(leadStatus).replace(/_/g, ' ')}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[72px] resize-y"
                  value={postCallNotes}
                  onChange={(e) => setPostCallNotes(e.target.value)}
                  placeholder="Optional — appears on the lead activity feed with this call"
                  disabled={postCallSaving}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => dismissPostCallPanel()}
                  disabled={postCallSaving}
                >
                  Skip
                </Button>
                <Button
                  type="button"
                  className="flex-1"
                  onClick={handlePostCallSaveClick}
                  isLoading={postCallSaving}
                  disabled={postCallSaving}
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-gray-400 text-center">
            Using {dialerLabel}. Ensure microphone permission is allowed in your browser.
          </p>
        </div>
      </Card>

      <DncConfirmModal
        isOpen={postCallDncOpen}
        onCancel={() => setPostCallDncOpen(false)}
        onConfirm={() => {
          setPostCallDncOpen(false);
          void commitPostCallSave();
        }}
        leadName={leadName || 'Lead'}
      />
    </div>
  );
}
